import { FC, useState } from 'react';
import { withPageAuthRequired } from '@auth0/nextjs-auth0/client';
import { useStripe } from '@stripe/react-stripe-js';

import { onlyCardPayment, showPaymentToastError } from 'components/checkout/payment/constants';
import { Payment } from 'components/checkout/payment/Payment';
import { YourWatch } from 'components/common/summary/YourWatch';
import { SlideoutBackButton } from 'components/design-system/Slideout';
import { Spinner } from 'components/design-system/Spinner';
import { ConditionLabelValueGroup } from 'components/listing_detail/ConditionLabelValueGroup';
import { pollListing } from 'components/listing_detail/v2/useRefreshListing';
import { useBezelUser } from 'context/bezelUser';
import { useModal } from 'context/modal/ModalContext';
import { useLogBidSubmitted } from 'hooks/analytics/useLogMaxBidSubmitted';
import { clientApiPost } from 'lib/api_helper';
import { withElements } from 'lib/stripe';
import styles from 'styles/Auctions.module.scss';
import { Listing, PaymentMethod, ProxyBid } from 'types';
import { formatPrice, getFormattedDate } from 'utils/text-helpers';

import { ActionButton } from './ActionButton';
import { BidSubmittedModal } from './BidSubmittedModal';
import { OutbidModal } from './OutbidModal';

import { BuyersPremiumInfo } from '../BuyersPremiumInfo';
import { fetchAndValidateProxyBids } from '../helpers';

type BidPaymentContentProps = {
  listing: Listing;
  goBack: () => void;
  bidValue: number;
  refreshListing: (listing: Listing) => void;
  refetchProxyBids: () => void;
  proxyBid?: ProxyBid;
};

const bidHoldPrice = formatPrice(75000);

const BidPaymentContentComponent: FC<BidPaymentContentProps> = ({
  goBack,
  listing,
  bidValue,
  refreshListing,
  refetchProxyBids,
  proxyBid
}) => {
  const [selectedPaymentMethod, setSelectedPaymentMethod] = useState<PaymentMethod | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const { closeModal, openModal } = useModal();
  const { user, refresh } = useBezelUser();
  const poll = pollListing(listing.id, refreshListing, listing);
  const logBidSubmitted = useLogBidSubmitted(listing, bidValue);

  const stripe = useStripe();
  const openBidSubmittedModal = () =>
    openModal(<BidSubmittedModal bidValue={bidValue} closeModal={closeModal} listing={listing} proxyBid={proxyBid} />);
  const openOutbidModal = (updatedListing: Listing) =>
    openModal(<OutbidModal closeModal={closeModal} listing={updatedListing} proxyBid={proxyBid} />);

  const placeBidHold = async () => {
    setIsLoading(true);
    if (!stripe) {
      showPaymentToastError();

      return;
    }

    try {
      const fetchedProxyBid: ProxyBid = await clientApiPost('/api/proxyBids', {
        listing: listing.id,
        buyerProfile: user?.buyerProfile?.id,
        maxPriceCents: bidValue,
        // remove after api fix
        client: 'bezel-web'
      });

      const { paymentIntent, error } = await stripe.confirmCardPayment(
        fetchedProxyBid.transaction.paymentInstructions.stripePaymentIntentClientSecret,
        {
          payment_method: selectedPaymentMethod?.details?.id
        }
      );

      if (error || (paymentIntent?.status !== 'succeeded' && paymentIntent?.status !== 'requires_capture')) {
        showPaymentToastError();
        setIsLoading(false);

        return;
      }

      if (!user?.buyerProfile?.id) {
        throw new Error('Failed to update proxy bid. User is not authenticated');
      }

      const { result, proxyBid: newProxyBid } = await fetchAndValidateProxyBids(user?.buyerProfile?.id, listing.id);

      if (result === 'success') {
        openBidSubmittedModal();
        logBidSubmitted();
      } else if (result === 'outbid') {
        openOutbidModal(newProxyBid?.listing);
      } else if (result === 'error') {
        throw new Error('Failed to update proxy bid');
      }
    } catch (error) {
      console.error(error);
      showPaymentToastError();
    } finally {
      setIsLoading(false);
      refetchProxyBids();
      poll();
    }
  };

  return (
    <div style={{ minHeight: '100%' }} className='d-flex text-light flex-column w-100 align-items-center'>
      <div className='w-100 pt-2'>
        <SlideoutBackButton isDark onClickPrevious={goBack} />
      </div>
      <h4 className='w-70 text-center fs-20px riforma-regular'>Bids are binding</h4>
      <div className='w-100 mt-4 mb-2 fs-16px line-height-24px riforma-regular line-height-24px'>Your max bid</div>
      <div className='w-100 border-top my-2 opacity-20' />
      <YourWatch isDark showPrice listing={listing} className='w-100 my-2'>
        <div className='text-light fs-12px'>
          <span className='opacity-60 pe-3 letter-spacing-08'>ENDS ON</span>
          <span className='letter-spacing-08'>{getFormattedDate(listing.auctionInfo.endDate)}</span>
        </div>
      </YourWatch>
      <div className='w-100 border-top my-2 opacity-20' />
      <div className='text-light mt-2 fs-14px gap-3 w-100'>
        <div className='d-flex justify-content-between align-items-center'>
          <span className='opacity-60'>Your bid</span>
          <span className='letter-spacing-08'>{formatPrice(bidValue)}</span>
        </div>
        <div className='my-2'>
          <BuyersPremiumInfo showFeeInTheEnd isDark className='text-secondary' />
        </div>
      </div>
      <div className='w-100 border-top my-2 opacity-20' />
      <div className='w-100 mt-3 mb-1 fs-16px line-height-24px riforma-regular'>Payment hold</div>
      <Payment
        isDark
        includeTitle={false}
        allowedPaymentTypes={onlyCardPayment}
        selectedPaymentMethod={selectedPaymentMethod}
        setSelectedPaymentMethod={setSelectedPaymentMethod}
        className='my-2 w-100'
        postSubmitNewCard={refresh}
      />
      <ConditionLabelValueGroup
        className={styles.lightBorderColor}
        label={<span className='fs-14px text-light'>{bidHoldPrice} bid hold</span>}
        value={
          <span className='fs-14px'>
            You’ll see a temporary {bidHoldPrice} hold on your card. If you win the auction, this hold will be removed
            once the final payment on the watch is received. If you lose, the hold will be removed.
          </span>
        }
      />
      <ConditionLabelValueGroup
        className={styles.lightBorderColor}
        label={<span className='fs-14px text-light'>Bids are binding</span>}
        value={
          <span className='fs-14px'>
            You have 24 hours to checkout out after winning. If you win the auction and do not pay, your hold will not
            be returned.
          </span>
        }
      />
      <div className='w-100 flex-1 d-flex align-items-center mb-5'>
        <ActionButton
          disabled={isLoading || !selectedPaymentMethod}
          onClick={placeBidHold}
          type='primary'
          className='w-100'
          content={
            isLoading ? (
              <div className='d-flex h-100 align-items-center justify-content-center'>
                <Spinner />
              </div>
            ) : (
              <div className='d-flex justify-content-between px-4'>
                <span>Confirm your bid</span>
                <span>{formatPrice(bidValue)}</span>
              </div>
            )
          }
        />
      </div>
    </div>
  );
};

export const BidPaymentContent = withPageAuthRequired(withElements(BidPaymentContentComponent));
